import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, RefreshCw, ArrowRight } from 'lucide-react';

/**
 * Iter47 — Owner-only "Review WO number sync" dialog.
 *
 * Presents every MP that has a REAL id-based Work Order connection but a
 * displayed `work_order_number` that is empty or conflicts with the
 * connected Work Order's number. Nothing is auto-changed. The Owner
 * ticks the rows they approve and clicks Apply.
 *
 * After apply, ONLY the displayed `work_order_number` is corrected —
 * no other field is touched. See services/wo_link.py.
 */
export function WoSyncReviewDialog({ open, onOpenChange, onApplied }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState({}); // {pid: true}
  const [busy, setBusy] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const load = () => {
    setItems(null); setSelected({}); setApplyResult(null);
    api.get('/master-projects/wo-link/display-audit')
      .then(r => setItems(r.data.items || []))
      .catch(e => { toast.error(parseErr(e)); setItems([]); });
  };

  useEffect(() => { if (open) load(); }, [open]);

  const toggleAll = (checked) => {
    if (!items) return;
    if (!checked) return setSelected({});
    const next = {};
    for (const it of items) next[it.project_id] = true;
    setSelected(next);
  };

  const checkedIds = Object.keys(selected).filter(k => selected[k]);
  const allChecked = items && items.length > 0 && checkedIds.length === items.length;

  const doApply = async () => {
    if (checkedIds.length === 0) {
      toast.error('Tick at least one row to apply.');
      return;
    }
    if (!window.confirm(`Apply displayed WO-number fix to ${checkedIds.length} project(s)? Only the displayed number changes — nothing else.`)) {
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        '/master-projects/wo-link/display-audit/apply',
        { approved_project_ids: checkedIds });
      setApplyResult(r.data);
      toast.success(`Synced ${r.data.updated_count} · skipped ${r.data.skipped_count}`);
      if (onApplied) onApplied();
      // Reload the audit — should shrink or empty.
      load();
    } catch (e) {
      toast.error(parseErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="wo-sync-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={16} /> Review WO number sync
          </DialogTitle>
        </DialogHeader>

        <div className="text-[13px] text-[hsl(var(--foreground-muted))] -mt-1 mb-2">
          These Master Projects have a <strong>real</strong> Work Order connection but the displayed number is either <strong>empty</strong> or <strong>conflicts</strong> with the Work Order&apos;s number. Tick the rows you want to correct. Only the displayed <em>work_order_number</em> is updated — no project, WO, financial, stage, trip, or history data is changed.
        </div>

        {items === null && (
          <div className="p-6 text-center text-[hsl(var(--foreground-muted))]" data-testid="wo-sync-loading">Loading…</div>
        )}

        {items && items.length === 0 && (
          <div className="border rounded-md p-6 text-center text-[13px] text-[hsl(var(--foreground-muted))]"
               style={{ borderColor: 'hsl(var(--border))' }}
               data-testid="wo-sync-empty">
            Every real WO connection is already showing the correct number. Nothing to review.
          </div>
        )}

        {items && items.length > 0 && (
          <div className="border rounded-md" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="grid grid-cols-[22px_1fr_auto_auto] gap-2 items-center px-3 py-2 border-b bg-[hsl(var(--surface-1))] text-[11.5px] uppercase tracking-wide text-[hsl(var(--foreground-muted))] font-semibold"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              <Checkbox checked={allChecked}
                         onCheckedChange={toggleAll}
                         data-testid="wo-sync-toggle-all" />
              <div>Project</div>
              <div>Displayed on MP</div>
              <div>Work Order</div>
            </div>
            <ul className="divide-y max-h-[380px] overflow-auto"
                style={{ borderColor: 'hsl(var(--border))' }}
                data-testid="wo-sync-list">
              {items.map(it => (
                <li key={it.project_id}
                    className="grid grid-cols-[22px_1fr_auto_auto] gap-2 items-center px-3 py-2 text-[12.5px] hover:bg-[hsl(var(--surface-1))]"
                    data-testid={`wo-sync-row-${it.project_id}`}>
                  <Checkbox checked={!!selected[it.project_id]}
                             onCheckedChange={(v) => setSelected(s => ({ ...s, [it.project_id]: !!v }))}
                             data-testid={`wo-sync-check-${it.project_id}`} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{it.project_name}</div>
                    <div className="text-[11px] text-[hsl(var(--foreground-muted))] truncate">
                      {it.client || '—'}{it.project_code ? ` · ${it.project_code}` : ''}
                    </div>
                  </div>
                  <div className="font-mono text-[11.5px] text-right"
                       style={{ color: it.diff_kind === 'empty' ? 'hsl(var(--warning))' : 'hsl(var(--danger))' }}>
                    {it.mp_wo_number_current || <em className="not-italic opacity-70">(empty)</em>}
                    <div className="text-[9.5px] uppercase tracking-wider">
                      {it.diff_kind === 'empty' ? 'to fill' : 'to fix'}
                    </div>
                  </div>
                  <div className="font-mono text-[11.5px] text-right">
                    <ArrowRight size={11} className="inline text-[hsl(var(--foreground-muted))] mr-1" />
                    {it.wo_number_from_wo}
                    <div className="text-[9.5px] uppercase tracking-wider text-[hsl(var(--success))]">from WO</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {applyResult && (
          <div className="border rounded-md p-3 mt-2 text-[12.5px]"
               style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-1))' }}
               data-testid="wo-sync-apply-result">
            <div className="font-medium">
              Synced {applyResult.updated_count} · Skipped {applyResult.skipped_count}
            </div>
            {applyResult.skipped_count > 0 && (
              <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5">
                Skipped reasons: {[...new Set((applyResult.skipped || []).map(s => s.reason))].join(', ')}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mr-auto flex items-center gap-1">
            <AlertTriangle size={11} /> Owner-only. Read the diff, tick, apply. Nothing else changes.
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wo-sync-close">Close</Button>
          <Button onClick={doApply}
                  disabled={busy || !items || items.length === 0 || checkedIds.length === 0}
                  data-testid="wo-sync-apply">
            {busy ? 'Applying…' : `Apply to ${checkedIds.length} row${checkedIds.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
